import React, { useMemo, useRef, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Download, Layers, ChevronRight } from "lucide-react";
import { AdPopup } from "@/components/AdPopup";
import AuthPill from "@/components/AuthPill";
import { useAuth } from "@/lib/auth";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const HERO_BG =
  "https://customer-assets.emergentagent.com/job_bfdcebf1-72f1-4caa-85a2-8a7993001623/artifacts/bze9wv63_E6F8C273-9D76-42B5-83AF-DD0B2C6475D0.png";

const INTENSITY = "mild";
const ACCENT = "#39FF14";

const ALLOWED = [".wav", ".mp3", ".m4a", ".flac", ".ogg", ".aac", ".aiff"];
const MAX_BYTES = 60 * 1024 * 1024;

export default function HomePage() {
  const [file, setFile] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [job, setJob] = useState(null);
  const [toast, setToast] = useState("");
  const fileInputRef = useRef(null);
  const { user } = useAuth();

  const processedUrl = useMemo(
    () => (job ? `${API}/fatboy/audio/${job.job_id}/processed` : null),
    [job]
  );

  const pickFile = (f) => {
    if (!f) return;
    const ext = "." + (f.name.split(".").pop() || "").toLowerCase();
    if (!ALLOWED.includes(ext)) {
      setToast("Wrong file type. Use WAV/MP3/M4A/FLAC.");
      return;
    }
    if (f.size > MAX_BYTES) {
      setToast("File too fat — 30 MB max.");
      return;
    }
    setFile(f);
    setJob(null);
    setToast("");
  };

  const handleBellyTap = async () => {
    if (processing) return;
    if (!file) {
      // First tap opens the file picker
      fileInputRef.current?.click();
      return;
    }
    if (job) {
      // Already processed — reset & regenerate
      setJob(null);
    }
    setToast("");
    setProcessing(true);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("intensity", INTENSITY);
      const resp = await axios.post(`${API}/fatboy/process`, form, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 300000, // 5 min — long tracks + ffmpeg loudnorm can take a while
      });
      setJob(resp.data);
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Processing failed.";
      setToast(typeof detail === "string" ? detail : "Processing failed.");
    } finally {
      setProcessing(false);
    }
  };

  const handleDownload = () => {
    if (!processedUrl) return;
    const a = document.createElement("a");
    a.href = processedUrl;
    a.download = `${(file?.name || "track").replace(/\.[^.]+$/, "")}_FATBOY_${INTENSITY}.mp3`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const bellyLabel = !file
    ? "PRESS BELLY TO UPLOAD"
    : job
    ? "PRESS AGAIN TO REGENERATE"
    : "PRESS BELLY TO GENERATE";

  return (
    <div
      className={`relative w-screen h-screen overflow-hidden bg-[#0A0A0A] ${
        processing ? "fb-screen-shake" : ""
      }`}
      data-testid="app-root"
    >
      {/* FULL SCREEN FATBOY — centered, full kid always visible (raised above any platform watermarks) */}
      <div
        className="absolute inset-0 flex items-center justify-center pointer-events-none"
        style={{ zIndex: 2147483600 }}
      >
        <div
          className="relative"
          style={{
            width: "min(100vh, 100vw)",
            height: "min(100vh, 100vw)",
          }}
        >
          <img
            src={HERO_BG}
            alt="FATBOY"
            draggable={false}
            className="absolute inset-0 w-full h-full object-cover md:object-contain pointer-events-none select-none"
            data-testid="fatboy-mascot-img"
          />

          {/* Belly tap button — positioned RELATIVE TO THE IMAGE so it lands on the belly at any aspect */}
          <button
            type="button"
            onClick={handleBellyTap}
            disabled={processing}
            aria-label={bellyLabel}
            data-testid="tap-belly-btn"
            className="absolute rounded-full cursor-pointer hover:scale-[1.03] active:scale-[0.93] transition-transform duration-150 disabled:cursor-wait pointer-events-auto"
            style={{
              left: "50%",
              top: "58%",
              transform: "translate(-50%, -50%)",
              width: "30%",
              height: "22%",
              background: "transparent",
              border: "none",
              zIndex: 2147483612,
            }}
          />

          {/* Subtle status hint — anchored near the tennis shoes */}
          <div
            className="absolute left-1/2 -translate-x-1/2 pointer-events-none w-[72%] md:w-[62%] text-center"
            style={{ top: "88%", zIndex: 2147483613 }}
          >
            <div
              className="font-graffiti text-[11px] md:text-sm uppercase tracking-widest text-white/90"
              style={{ textShadow: "0 1px 6px rgba(0,0,0,0.85)" }}
              data-testid="status-hint"
            >
              {processing
                ? "GETTING FAT…"
                : !file
                ? bellyLabel
                : job
                ? "DONE · HIT DOWNLOAD BELOW"
                : "PRESS THE BELLY"}
            </div>
          </div>
        </div>
      </div>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept={ALLOWED.join(",")}
        className="hidden"
        onChange={(e) => pickFile(e.target.files?.[0])}
        data-testid="audio-file-input"
      />

      {/* TOP BAR — logo + DUMMY STEMS link */}
      <div
        className="absolute top-0 left-0 right-0 p-4 md:p-6 flex items-start justify-between pointer-events-none"
        style={{ zIndex: 2147483610 }}
      >
        <div className="font-heading text-xl md:text-3xl tracking-tight text-white fb-drip pointer-events-auto">
          FAT<span className="text-[#FF2E9A]">BOY</span>
        </div>

        <Link
          to="/stems"
          data-testid="nav-dummy-stems"
          className="pointer-events-auto font-heading text-xs md:text-sm tracking-widest uppercase text-white/85 hover:text-[#FF2E9A] transition-colors flex items-center gap-2 px-3 py-2 rounded-full bg-black/30 backdrop-blur-md border border-white/15"
        >
          <Layers size={14} strokeWidth={2.5} />
          DUMMY STEMS
          <ChevronRight size={14} strokeWidth={2.5} />
        </Link>
      </div>

      {/* AUTH PILL — top-right, below the DUMMY STEMS chip */}
      <div
        className="absolute top-16 md:top-20 right-4 md:right-6 pointer-events-none"
        style={{ zIndex: 2147483611 }}
      >
        <AuthPill accent="#FF2E9A" />
      </div>

      {/* File name (translucent, minimal) */}
      {file && (
        <div
          className="absolute top-20 md:top-24 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-black/35 backdrop-blur-md text-white/85 font-body text-[11px] md:text-xs uppercase tracking-widest max-w-[80vw] truncate pointer-events-none"
          style={{ zIndex: 2147483610 }}
          data-testid="selected-file-name"
        >
          {file.name}
        </div>
      )}

      {/* BOTTOM BAR — download / error only (status + spice picker are anchored on the kid's shoes) */}
      <div
        className="absolute bottom-0 left-0 right-0 p-4 md:p-6 flex flex-col items-center gap-3 pointer-events-none"
        style={{ zIndex: 2147483610 }}
      >
        {job && (
          <button
            type="button"
            onClick={handleDownload}
            data-testid="download-audio-btn"
            className="pointer-events-auto fb-btn bg-white text-black py-3 md:py-4 px-6 md:px-10 font-heading text-sm md:text-xl flex items-center gap-2 uppercase"
          >
            <Download size={20} strokeWidth={3} />
            DOWNLOAD THE FAT ONE
          </button>
        )}

        {toast && (
          <div
            className="pointer-events-auto fb-shake font-heading text-xs md:text-sm bg-red-500 text-white px-4 py-2 border-2 border-white"
            data-testid="process-error"
          >
            {toast}
          </div>
        )}
      </div>

      {/* Processing flash */}
      {processing && (
        <div
          className="absolute inset-0 pointer-events-none mix-blend-screen"
          style={{
            background: `radial-gradient(circle at 50% 58%, ${ACCENT}55, transparent 55%)`,
            zIndex: 2147483605,
          }}
          data-testid="processing-flash"
        />
      )}

      <div
        className="fb-grain absolute inset-0 pointer-events-none"
        style={{ zIndex: 2147483601 }}
      />

      {/* AdPopup is skipped for VIP founding members + anyone with an
          active "no ads" pass. See NoAdsModal for how users buy it. */}
      {!user?.ads_removed && <AdPopup />}
    </div>
  );
}
